/**
 * ThemeProvider - Loads tenant theme and injects CSS custom properties
 *
 * Fetches theme JSON from /themes/<tenantId>.json (public folder for now,
 * backend API later). Injects all design tokens as CSS custom properties
 * on :root so Tailwind and components consume them transparently.
 */
import React, { createContext, useContext, useEffect, useState } from 'react'
import type { TenantTheme } from './types'

interface ThemeContextValue {
  theme: TenantTheme | null
  loading: boolean
  error: string | null
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: null,
  loading: true,
  error: null,
})

export const useTheme = () => useContext(ThemeContext)

/**
 * Resolve tenant ID from hostname.
 * For now, hardcoded mapping. Later this comes from backend bootstrap.
 */
function resolveTenantId(): string {
  const hostname = window.location.hostname
  if (hostname.includes('devsquad')) {
    return 'devsquad'
  }
  // localhost uses default theme unless subdomain hints at a tenant
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    const params = new URLSearchParams(window.location.search)
    return params.get('tenant') || 'default'
  }
  return 'default'
}

/**
 * Inject theme tokens as CSS custom properties on :root
 */
function applyThemeToDOM(theme: TenantTheme): void {
  const root = document.documentElement

  // Colors
  root.style.setProperty('--color-primary', theme.colors.primary)
  root.style.setProperty('--color-primary-hover', theme.colors.primaryHover)
  root.style.setProperty('--color-primary-light', theme.colors.primaryLight)
  root.style.setProperty('--color-primary-glow', theme.colors.primaryGlow)
  root.style.setProperty('--color-surface', theme.colors.surface)
  root.style.setProperty('--color-surface-elevated', theme.colors.surfaceElevated)
  root.style.setProperty('--color-surface-higher', theme.colors.surfaceHigher)
  root.style.setProperty('--color-surface-border', theme.colors.surfaceBorder)
  root.style.setProperty('--color-text-primary', theme.colors.textPrimary)
  root.style.setProperty('--color-text-secondary', theme.colors.textSecondary)
  root.style.setProperty('--color-text-muted', theme.colors.textMuted)
  root.style.setProperty('--color-card-bg', theme.colors.cardBg)
  root.style.setProperty('--color-card-text', theme.colors.cardText)
  root.style.setProperty('--color-card-text-secondary', theme.colors.cardTextSecondary)
  root.style.setProperty('--color-card-border', theme.colors.cardBorder)
  root.style.setProperty('--color-success', theme.colors.success)
  root.style.setProperty('--color-error', theme.colors.error)
  root.style.setProperty('--color-warning', theme.colors.warning)
  root.style.setProperty('--color-info', theme.colors.info)

  // Fonts
  root.style.setProperty('--font-heading', theme.fonts.heading)
  root.style.setProperty('--font-body', theme.fonts.body)

  // Border radius
  root.style.setProperty('--radius-sm', theme.borderRadius.sm)
  root.style.setProperty('--radius-md', theme.borderRadius.md)
  root.style.setProperty('--radius-lg', theme.borderRadius.lg)
  root.style.setProperty('--radius-xl', theme.borderRadius.xl)

  // Legacy tokens for backward compatibility
  root.style.setProperty('--primary-color', theme.colors.primary)
  root.style.setProperty('--accent-color', theme.colors.primaryLight)
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<TenantTheme | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const tenantId = resolveTenantId()

    fetch(`/themes/${tenantId}.json`)
      .then((res) => {
        if (!res.ok) throw new Error(`Theme not found: ${res.status}`)
        return res.json()
      })
      .then((data: TenantTheme) => {
        applyThemeToDOM(data)
        setTheme(data)
        setLoading(false)
      })
      .catch((err) => {
        console.warn(`Failed to load theme for tenant "${tenantId}":`, err)
        setError(err.message)
        setLoading(false)
      })
  }, [])

  // Show nothing until theme loads (prevents flash of unstyled content)
  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#F8FAFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            border: '3px solid #E2E8F0',
            borderTopColor: '#94A3B8',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    )
  }

  return (
    <ThemeContext.Provider value={{ theme, loading, error }}>
      {children}
    </ThemeContext.Provider>
  )
}
